import { Json, type JsonObject, type Option, type Timescale } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import { AsyncStateManager } from "@tento-core/async-state-manager";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { mutate } from "@tento-ui/fastdom";
import { Lisp } from "@tento-lisp/lisp";
import { FormulaIcon, LonaIcons } from "@tento-ui/ui/icons";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type OptionsInfo,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { SheetViewModel } from "@tento-lona";
import { type SheetRow } from "@tento-lona";
import { NumberFormatter } from "@tento-ui/number-formatter";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * The source-row plugin for a formula row: its expression and computed
 * values.
 */
export class FormulaSourcePlugin implements RenderPlugin.SourceRow<FormulaElement, SheetLegend.DefaultRow> {
  readonly role = "source";
  readonly id = "formula";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(FormulaIcon);

  #stateManager = new AsyncStateManager();

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly loadData: Option<
      (
        sheet: SheetViewModel.Surface<SheetBinder>,
        row: SheetRow.Joined.Handle,
        column: NaiveDate.Partial,
      ) => Promise<unknown>
    >,
    public readonly onRowLearnMore: Option<() => void>,
  ) {}

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string | OptionsInfo> {
    const onRowLearnMore = this.onRowLearnMore;
    const $message = document.createElement("span");
    $message.append(
      "Use formulas to generate cells on the fly. A simple program like ",
    );
    const $code = document.createElement("span");
    $code.textContent = "(+ 1 2)";
    $code.style.fontFamily = "monospace";
    $code.style.background = "var(--bg-surface-light)";
    $code.style.padding = "2px 4px";
    $code.style.borderRadius = "4px";
    $message.append($code);
    $message.append(" will generate 3.");
    return {
      message: $message,
      ...(onRowLearnMore ? { onLearnMore: () => onRowLearnMore() } : {}),
    };
  }

  makeCell(): FormulaElement {
    return FormulaElement.make();
  }

  async bindCell(
    $cell: FormulaElement,
    { row, column }: BindCellContext,
  ) {
    const time = column.discrete();
    const cellHandle = row.single()?.cell(time[0]);
    if (cellHandle == null) return;
    const stillValid = this.#stateManager.bind($cell, cellHandle);
    cellHandle.onDataFull((cell) => {
      if (!stillValid(cellHandle)) return;
      const data = (cell?.value ?? null) as Option<Lisp.Atom>;
      $cell.bind(
        data,
        row.variant?.attributesAs(FormulaSourcePlugin.decodeAttributes) ?? null,
      );
    });
  }

  onResize(_$cell: FormulaElement, _context: BindCellContext): void {}

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }
    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
      sheetTimescale: Timescale,
    ) {
      $legend.toggleAttribute("disabled", row.hideLegend ?? false);
      if (row.hideLegend) return;
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        { sheetTimescale },
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}
}

/** Types and values that go with `FormulaSourcePlugin`: `Attributes`, `decodeAttributes`. */
export namespace FormulaSourcePlugin {
  /** A formula row's presentation as `decodeAttributes` reads it: `null` where the bag does not say. */
  export type Attributes = {
    readonly style: Option<"flat" | "default">;
    readonly size: Option<"small" | "default">;
  };

  const STYLES: readonly ("flat" | "default")[] = ["flat", "default"];
  const SIZES: readonly ("small" | "default")[] = ["small", "default"];

  /** Read a formula row's `style` / `size` off its bag. */
  export function decodeAttributes(attributes: JsonObject): Attributes {
    return {
      style: Json.oneOf(attributes, "style", STYLES),
      size: Json.oneOf(attributes, "size", SIZES),
    };
  }
}

/** The `std-formula` element: a formula row's expression and result. */
@component({
  name: "std-formula",
})
export class FormulaElement extends LonaWebComponent {
  @mutate
  bind(
    atom: Option<Lisp.Atom>,
    attributes: Option<FormulaSourcePlugin.Attributes>,
  ) {
    const $children = [...this.children] as FormulaCell[];
    const atoms = Array.isArray(atom) ? atom : [atom];
    this.style.setProperty("--num-children", String(atoms.length));

    for (const [idx, a] of atoms.entries()) {
      const $formula = $children[idx] ?? FormulaCell.make();
      $formula.bind(a, attributes?.size ?? "default");
      $formula.toggleAttribute("hidden", false);
      $formula.style.removeProperty("display");
      this.appendChild($formula);
    }

    for (const $extra of $children.slice(atoms.length)) {
      $extra.style.display = "none";
    }
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: grid;
        grid-template-columns: repeat(var(--num-children), 1fr);
        grid-auto-flow: column;
        gap: 0px;
        height: 100%;
        margin: 0px 1px;
      }

      slot::slotted(*) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html` <slot></slot> `;
}

/** One cell of a formula row. */
@component({
  name: "std-formula-cell",
})
export class FormulaCell extends LonaWebComponent {
  bind(data: Option<Lisp.Atom>, size: string) {
    this.$("root").assignStyles({
      "--font-size":
        size === "small" ? "var(--small-size)" : "var(--small-size)",
    });

    if (data == null) {
      this.textContent = "—";
    } else if (typeof data === "object") {
      this.textContent = JSON.stringify(data);
    } else if (typeof data === "number") {
      const formatted = NumberFormatter.formatters.get().default;
      this.textContent = NumberFormatter.format(formatted, data);
    } else {
      this.textContent = String(data);
    }
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 100%;
        padding: 4px;
        padding-right: 1px;

        height: 100%;
        color: var(--text-ter);
      }

      #root {
        overflow: hidden;
        white-space: nowrap;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <p id="root">
      <slot></slot>
    </p>
  `;
}
